import { ApiEndpoint } from './ApiDefinition';
import { JSON, JSONArray, JSONDateTime, JSONObject } from './types';


export const AuthApi = {
  getADClientId: ApiEndpoint<void, string>({
    url: 'security/ad-client-id'
  }),

  login: ApiEndpoint<{
    email: string,
    password: string
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/login',
    body: ({ email, password }) => ({
      email: email || "",
      password: password || ""
    })
  }),

  verifyMfa: ApiEndpoint<{
    otp: string,
    token?: string
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/verify-mfa',
    body: ({ otp, token }) => ({
        options: { 
            otp: otp || "",
        },
        token: token || ""
    })
  }),

  impersonate: ApiEndpoint<{
    userId: number
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/impersonate',
    body: ({ userId }) => ({
      userId: userId,
    })
  }),

  loginWithToken: ApiEndpoint<{
    token: string
  }, AuthenticateResponse>({
    method: 'post',
    url: 'security/login-with-token',
    body: ({ token }) => ({
      data: { token },
    })
  }),

  verifyToken: ApiEndpoint<void, boolean>({
    method: 'post',
    url: 'security/check'
  }),

  recoverPassword: ApiEndpoint<{ email: string }, string>({
    method: 'post',
    url: ({ email }) => `security/password-recovery/${email}`
  }),

  resetPassword: ApiEndpoint<{
    token: string,
    password: string,
    passwordConfirm: string
  }, string>({
    method: 'post',
    url: ({ token }) => `security/password-reset/${token}`,
    body: ({ password, passwordConfirm }) => ({
      data: {
        password,
        passwordConfirm
      }
    })
  }),

  getSelf: ApiEndpoint<void, UserDetailsDTO>({
    url: 'security/self'
  }),

  getSignature: ApiEndpoint<void, any>({
    url: 'security/self/signature'
  }),

  updateSelf: ApiEndpoint<UpdateSelfUserDTO, UserDetailsDTO>({
    method: 'post',
    url: 'security/self',
    body: data => data
  }),

};


export enum MfaChallengeType {
  EMAIL_OTP = 0
};

export type UpdateSelfUserDTO = {
  firstName: string
  lastName: string
  phone: string
  extension: string
  password: string
  passwordConfirm: string
  signature: string
  notifyDesktop: boolean
  notifyEmail: boolean
  timeZoneId: string
  isOnLeave: boolean
}

export type AuthenticateResponse = {
  user: UserAuthDTO
  token: string
  logOutUrl: string
  mfaVerificationRequired?: boolean
  mfaVerificationType?: MfaChallengeType
}

export type UserDetailsDTO = {
    id: number
    email: string
    createdAt?: JSONDateTime
    isActive: boolean
    isOnLeave: boolean
    hasCompletedProfile: boolean
    changePassword: boolean
    firstName: string
    lastName: string
    organizationalUnitId: number
    phone: string
    extension: string
    position: string
    jediId: number
    accessProfileId: number
    signature: string
    organizationalUnitRoles: UserOrganizationalUnitRoleDTO[]
    branchId: number
    extras: JSON
    notifyDesktop: boolean
    notifyEmail: boolean
    timeZoneId: string
};

export type UserOrganizationalUnitRoleDTO = {
  id: number
  userId: number
  roleId: number
  organizationalUnitId: number
  mainRole: boolean
  canWorkAs: boolean
  canBeAssigned: boolean
  canAssign: boolean
  canWorkUnassigned: boolean
}

export type UserAuthDTO = {
  id: number,
  firstName: string,
  lastName: string,
  email: string,
  jediId?: number,
  createdAt?: JSONDateTime,
  organizationalUnit: OrganizationalUnitInfoDTO,
  accessProfile: AccessProfileDTO,
  organizationalUnitRoles: UserOrganizationalUnitRoleAuthDTO[],
  isActive: boolean,
  isDual: boolean,
  isSuperuser: boolean,
  hasCompletedProfile: boolean,
  changePassword: boolean,
  position: string,
};
export type OrganizationalUnitInfoDTO = {
  id: number
  tenantId: number
  parentId?: number
  removed: boolean
  code: string
  name: string
  address1: string
  address2: string
  city: string
  postalCode: string
  state: string
  path: string
  numericCode: number
  dependencyFigure: number
  type: string
  workflow: WorkflowDTO
  workflowParams: JSONObject
};
export type WorkflowDTO = {
    id: number
    objectSchemaId: number
    name: string
    description: string
    slug: string
    redirect: string
    viewForm: string
    definition: JSONObject
    paramSchema: JSONObject
    roles: JSONArray
    version: number
    lastUpdated: JSONDateTime
    workflowLayout: JSONObject
};

export type AccessProfileDTO = {
  name: string
  displayName: string
  permissions: string[]
}
export type UserOrganizationalUnitRoleAuthDTO = {
  id: number
  userId: number
  roleId: number
  organizationalUnitId: number
  role: RoleDTO
  organizationalUnit: OrganizationalUnitInfoDTO
  mainRole: boolean
  canWorkAs: boolean
  canBeAssigned: boolean
  canAssign: boolean
  canWorkUnassigned: boolean
};
export type RoleDTO = {
  name: string
  displayName: string
  extras: JSONObject
  entities: JSONObject
  removed: boolean
}



export default AuthApi;
